<template>
  <div
    class="tree"
    :class="{
      'tree__option__connection-brothers': parent?.action == BotActions.ContinueConnectorApi
    }"
    v-for="(dataSource, nodeIndex) in nodeList" :key="dataSource.id"
  >
    <message-node
      v-if="nodeTypeMessage.includes(dataSource.type)"
      @onDelete="handleShowConfirmToDeleteMessage(dataSource)"
      @onSelect="handleOpenDrawer(dataSource)"
      class="tree__width"
      :id="`message-${nodeIndex}`"
      :class="{ tree__connection: dataSource.node }"
      :data-source="dataSource"
      :description="dataSource.message"
      :action="formattedAction(dataSource)"
      :selected="selectedNodeId === dataSource.id ? true : false"
      :showInfo="dataSource.action === BotActions.FinishChatbot ? true : false"
      :disabled="disabledActions || isChatbotActive"
      @onPreviewMedia="handleShowMedia"
    />
    <div
      :class="{
        'tree__option__connection-brothers':
          parent?.child?.length > 1 && nodeIndex !== parent?.child?.length - 1,
      }"
      v-if="
        hasChildNodes(dataSource.type) || 
        (dataSource?.action == BotActions.ContinueConnectorApi && dataSource.child?.length > 0)
      "
    >
      <options-node
        v-if="!nodeTypeMessage.includes(dataSource.type)"
        @onDelete="
          [NodeType.ListSection, NodeType.CardSection].includes(dataSource.type)
            ? handleShowConfirmToDeleteOption(dataSource, parent)
            : handleShowConfirmToDeleteOptions(dataSource)
        "
        @onAddOption="handleAddOption(dataSource)"
        @onSelect="handleOpenDrawer(dataSource)"
        @onChangeOrder="handleChangeOrderOption(dataSource, $event, parent)"
        @preview="handlePreview"
        class="tree__width"
        :class="{
          tree__connection: dataSource.node,
          tree__option__connection: nodeIndex > 0,
          simple__node__connection: dataSource.type === NodeType.Card,
        }"
        :action="formattedAction(dataSource)"
        :addingNewNode="addingNewNode"
        :dataSource="dataSource"
        :totals="getTotalChild(dataSource)"
        :quantity-orders="parent?.child?.length"
        :order="dataSource?.order"
        :description="dataSource.message || dataSource.description"
        :selected="selectedNodeId === dataSource.id ? true : false"
        :disabled="disabledActions || isChatbotActive"
        :isAllowedChange="isSavedNode"
        :disabledChangeOrder="parent?.child?.length === 1 || isChatbotActive"
        :disabledAddChild="isDisabledAddChild(dataSource)"
        :currentChannel="currentChannel"
        :showInfo="dataSource.action === BotActions.FinishChatbot"
        :parent="parent"
        @onPreviewMedia="handleShowMedia"
      />
      <div
        v-if="![NodeType.List, NodeType.Card].includes(dataSource.type) && dataSource.child?.length"
        class="tree__options"
        :class="{ tree__child: parent?.child?.length > 1 }"
      >
        <div
          v-for="(option, index) in dataSource.child"
          :key="index"
          :class="{
            'tree__option__connection-brothers': dataSource.child[index + 1],
            tree__option__first: index === 0 ? true : false,
          }"
        >
          <template v-if="!nodeTypeMessage.includes(option.type) &&
              ![NodeType.Buttons, NodeType.List, NodeType.Options].includes(option.type)">
            <option-node
              v-if="option.type !== NodeType.ListSection"
              @onDelete="handleShowConfirmToDeleteOption(option, dataSource)"
              @onSelect="handleOpenDrawer(option)"
              @onChangeOrder="handleChangeOrderOption(option, $event, dataSource)"
              :description="option?.title || option?.description"
              :order="option?.order"
              :quantity-orders="dataSource.child.length"
              :parent="dataSource"
              :data-source="option"
              class="tree__width tree__option"
              :class="{ tree__option__connection: option?.node }"
              :selected="selectedNodeId === option?.id ? true : false"
              :show-delete="option?.order === 1 ? false : true"
              :action="formattedAction(option)"
              :isAllowedChange="isSavedNode"
              :disabled="disabledActions || isChatbotActive || dataSource.child.length === 1"
              @onPreviewMedia="handleShowMedia"
            />
            <!-- {{ option.child?.[0] }} -->
            <div class="tree__child" v-if="option.child?.[0]">
              <tree
                :nodeList="option?.child"
                :deepLevel="deepLevel + 1"
                :parent="option"
                @onOpenDrawer="handleOpenDrawer"
                @preview="handlePreview"
                :disabled-actions="disabledActions || isChatbotActive"
                :currentChannel="currentChannel"
                @onPreviewMedia="handleShowMedia"
              />
            </div>
          </template>
          <template v-else>
            <tree
              :nodeList="[option]"
              :deepLevel="deepLevel + 1"
              :parent="dataSource"
              @onOpenDrawer="handleOpenDrawer"
              @preview="handlePreview"
              :disabled-actions="disabledActions || isChatbotActive"
              :currentChannel="currentChannel"
              @onPreviewMedia="handleShowMedia"
            />
          </template>
        </div>
      </div>
    </div>
    <div
      class="tree__child test"
      v-if="
        ![NodeType.Options, NodeType.ListSectionItem, NodeType.ButtonOption, NodeType.WrongOptionAPI, NodeType.OptionConsultApi].includes(
          dataSource.type
        ) && dataSource.child?.[0] && dataSource?.action != BotActions.ContinueConnectorApi
      "
    >
      <tree
        :nodeList="dataSource.child"
        :deepLevel="deepLevel + 1"
        :parent="dataSource"
        @onOpenDrawer="handleOpenDrawer"
        @preview="handlePreview"
        :disabled-actions="disabledActions || isChatbotActive"
        :currentChannel="currentChannel"
        @onPreviewMedia="handleShowMedia"
      />
    </div>
  </div>
</template>


<script>
import { createVNode, inject, ref } from "vue";
import { mapGetters, mapActions, mapMutations } from "vuex";
import { QuestionCircleOutlined } from "@ant-design/icons-vue";

import { NodeType, BotActions, DefaultNodeBodies, ChildNodeType } from "@/app/chatbots/utils/enums";
import { deleteNode, updateNode } from "@/app/chatbots/services";
import { hasChildNodes } from "@/app/chatbots/utils/helpers";
import { AcronymByNode, DeleteTextByNode } from "@/app/chatbots/utils/texts";
import { executeActionOnNodesBellow } from "@/app/chatbots/utils/varsTree";

import MessageNode from "./MessageNode.vue";
import OptionNode from "./OptionNode.vue";
import OptionsNode from "./OptionsNode.vue";
import { ChannelType } from "@/app/shared/utils/enums";

export default {
  name: "Tree",
  components: {
    MessageNode,
    OptionNode,
    OptionsNode,
  },
  props: {
    dataSource: {
      type: Object,
      default: () => ({
        drawer: {
          options: [],
        },
      }),
    },
    disabledActions: {
      type: Boolean,
      default: false,
    },
    deepLevel: {
      type: Number,
      default: 1,
    },
    parent: {
      type: Object,
    },
    nodeList: { type: Array, default: () => [] },
    currentChannel: { type: Object, default: () => ({}) },
  },
  setup() {
    const addingNewNode = ref(false);

    const isChatbotActive = inject("isChatbotActive");
    const functionsToChatbot = inject("functionsToChatbot");

    return {
      NodeType,
      BotActions,
      isChatbotActive,
      hasChildNodes,
      functionsToChatbot,
      addingNewNode,
    };
  },
  inject: ["handleCreateNode"],
  computed: {
    ...mapGetters(["nodes", "selectedNodeId", "isSavedNode"]),

    chatbotId() {
      return this.$route.params.id;
    },
    /**
     * @return String[] - tipos del nodo mensaje
     */
    nodeTypeMessage() {
      return [
        NodeType.Message,
        NodeType.MessageWithImage,
        NodeType.MessageWithDocument,
        NodeType.MessageWithVideo,        
      ];
    },
  },
  methods: {
    ...mapActions(["deleteOption", "addOptionToNode", "updateNodeMessage", "updateOption"]),

    ...mapMutations(["SET_SELECTED_NODE_ID"]),
    /**
     * Elimina un nodo mensaje u opciones
     * @param {String} nodeId - id del nodo
     * @param {String} parentNodeId - id de su padre
     */
    async fetchNodes() {
      this.$store.dispatch("fetchNodes", this.$route.params.id);
    },
    handlePreview(imageUrl) {
      this.$emit("preview", imageUrl);
    },
    async handleDeleteNode(currentNode, parentNode = null) {
      console.log("handleDelete", currentNode);
      if (hasChildNodes(parentNode?.type)) {
        const followingOptions = parentNode.child.filter(
          (option) => option.order > currentNode.order
        );

        if (followingOptions.length !== 0) {
          await Promise.allSettled(
            followingOptions.map((option) => updateNode(option.id, { order: option.order - 1 }))
          );
        }
      } else if (currentNode.node) {
        await updateNode(currentNode.node, { action: BotActions.FinishChatbot });
      }

      try {
        await deleteNode(currentNode.id);
        // despues de eliminado se remueven todas las variables del mismo nodo y de las siguientes
        executeActionOnNodesBellow(currentNode, (node) => {
          console.log("execute", node);
          node.variable && this.functionsToChatbot.onDeleteVar(node.variable.variable.id);
        });

        if (this.selectedNodeId === currentNode.node) {
          await this.$store.dispatch("fetchNodeDetails", this.selectedNodeId);
        }
        this.$message.success("Se eliminó el nodo");
        await this.fetchNodes();
      } catch (error) {
        const errorMessage = error?.getErrorMessage() || "Error al eliminar";
        this.$message.error(errorMessage);
        console.log(error);
      }
    },
    /**
     * Elimina una opcion
     * @param {String} nodeId - id de su nodo padre
     * @param {String} idOption - id de la opcion
     */
    async handleDeleteNodeOption(nodeId, idOption) {
      await this.deleteOption({ chatbotId: this.chatbotId, nodeId, idOption });
    },
    getNodeChildrenLimit(node) {
      const limitPerType = {
        [NodeType.Options]: 20,
        [NodeType.Buttons]: this.currentChannel.id === ChannelType.Whatsapp ? 3 : 13,
        [NodeType.List]: 10,
        [NodeType.ListSection]: 10,
        [NodeType.CardSection]: 3,
      };

      return limitPerType[node.type];
    },
    isDisabledAddChild(node) {
      return this.getCurrentLength(node) >= this.getNodeChildrenLimit(node);
    },
    getCurrentLength(parentNode) {
      const { items } = this.getTotalChild(
        parentNode.type === NodeType.ListSection ? this.parent : parentNode
      );
      const currentLength = {
        [NodeType.Options]: parentNode.child.length,
        [NodeType.Buttons]: parentNode.child.length,
        [NodeType.List]: items,
        [NodeType.ListSection]: items,
        [NodeType.CardSection]: parentNode.child.length,
      };

      return currentLength[parentNode.type];
    },
    getTotalChild(node) {
      const sections = node.child?.length ?? 0;
      const items = node.child?.reduce((prev, curr) => prev + curr?.child.length ?? 0, 0);

      return { sections, items };
    },
    /**
     * Añade una opcion a las opciones
     * @param {String} parentNodeId - nodo
     */
    async handleAddOption(parentNode) {
      console.log(parentNode);

      if (this.getCurrentLength(parentNode) >= this.getNodeChildrenLimit(parentNode))
        return this.$message.error("Se alcanzó el máximo de opciones 😥");

      this.addingNewNode = true;

      const childType = ChildNodeType[parentNode.type];

      const newOrder = (parentNode.child?.at(-1)?.order ?? 0) + 1;

      const newOptionBody = {
        ...DefaultNodeBodies[childType],
        order: newOrder,
        node: parentNode.id,
      };

      if (newOptionBody.title) {
        const newTitle = newOptionBody.title.replace(/[0-9]+/g, newOrder);

        newOptionBody.title = newTitle;
      }

      try {
        // await this.$store.dispatch("createNode", {
        //   chatbotId: this.$route.params.id,
        //   body: newOptionBody,
        // });
        await this.handleCreateNode({
          nodeType: newOptionBody.type,
          parentNodeId: parentNode.id,
          newBody: newOptionBody,
        });

        // this.$message.success("Se añadio correctamente");
        await this.fetchNodes();
      } catch (error) {
        const errorMessage = error?.getErrorMessage() || `No pudimos agregar la opción 😓`;
        this.$message.error(errorMessage);
        console.log(error);
      }

      this.addingNewNode = false;
    },
    /**
     * Emite la funcion de abrir el drawer de configuracion
     * @param {Object} node - puede ser el nodo de opciones, mensaje u opcion
     * @param {String} node._id - id del nodo
     * @param {String} node.idOption - id de la opcion
     */
    handleOpenDrawer(node) {
      if (this.isSavedNode) {
        this.$emit("onOpenDrawer", node);

        // Just set the selected node and fetch its details when the event propagates to the first level of the tree
        if (this.deepLevel === 1) {
          this.SET_SELECTED_NODE_ID(node.id || node.idOption);
          this.$store.dispatch("fetchNodeDetails", node.id);
        }
      } else this.$message.warning("Es necesario guardar los cambios");
    },
    /**
     * Cambia el orden de las opciones
     * @param {Object} option - la opcion a cambiar de posicion
     * @param {Number} newOrder - el orden al cual se quiere cambiar
     * @param {Object} dataSource - la data del nodo padre de los singleOptions
     */
    async handleChangeOrderOption(option, newOrder, dataSource) {
      if (!this.isSavedNode) this.$message.warning("Es necesario guardar los cambios");

      const [secondOption] = dataSource.child.filter((opt) => opt.order === newOrder);

      try {
        await Promise.all([
          updateNode(option.id, { order: newOrder }),
          updateNode(secondOption.id, { order: option.order }),
        ]);

        this.$message.success("Se cambió el orden correctamente");
        await this.fetchNodes();
      } catch (error) {
        const errorMessage = error?.getErrorMessage() || `No pudimos cambiar el orden 😓`;
        this.$message.error(errorMessage);
        console.log(error);
      }
    },
    /**
     * Formatea una accion
     * @param {String} action - none, continue, transfer, rollback
     * @param {String} nodeType - message, options
     * @param {Boolean} wait - mensaje con espera o  sin espera
     */
    formattedAction(node) {
      const { wait, action, node_to_back, node_to_back_type } = node;

      if (wait && action !== BotActions.FinishChatbot) return "Esperar una respuesta";
      switch (action) {
        case BotActions.AddMessage:
          return "Continuar al siguiente mensaje";
        case BotActions.ContinueToOptions:
          return "Continuar a las opciones";
        case BotActions.FinishChatbot:
          return "Finalizar chatbot";
        case BotActions.TransferToQueue:
          return "Transferir a cola";
        case BotActions.ContinueToButtons:
          return "Continuar con botones";
        case BotActions.ContinueToList:
          return "Continuar con lista";
        case BotActions.ContinueToCard:
          return "Continar con tarjetas";
        case BotActions.BackToPreviousMenu:
          return `Volver al nodo (${AcronymByNode[node_to_back_type]}${node_to_back})`;
        case BotActions.ContinueConnectorApi:
          return `Consulta API`;
        default:
          return "Siguiente";
      }
    },
    /**
     * Modal de confirmacion
     * @param {String} title
     * @param {String} content
     * @param {String} okText
     * @param {String} cancelText
     * @param {Function} onOkey
     * @param {Function} onCancel
     */
    handleShowConfirm(title, content, okText, fnOk) {
      this.$confirm({
        title: title,
        icon: createVNode(QuestionCircleOutlined),
        content: content,
        okText: okText,
        cancelText: "Cancelar",
        onOk() {
          fnOk();
        },
      });
    },
    handleShowConfirmToDeleteMessage(currentNode) {
      this.handleShowConfirm(
        DeleteTextByNode[currentNode.type],
        "Recuerda que se eliminarán todas sus configuraciones dependientes.",
        "Eliminar",
        () => this.handleDeleteNode(currentNode)
      );
    },
    handleShowConfirmToDeleteOptions(currentNode) {
      this.handleShowConfirm(
        DeleteTextByNode[currentNode.type],
        "Recuerda que se elimanarán todas las opciones pertenecientes así como sus configuraciones.",
        "Eliminar",
        () => this.handleDeleteNode(currentNode)
      );
    },
    handleShowConfirmToDeleteOption(currentNode, parentNode) {
      this.handleShowConfirm(
        DeleteTextByNode[currentNode.type],
        "Recuerda que se eliminará su configuración y se removerá del listado de opciones múltiples.",
        "Eliminar",
        () => this.handleDeleteNode(currentNode, parentNode)
      );
    },
    handleShowMedia(media) {
      console.log("emit", media);
      this.$emit("onPreviewMedia", media);
    },
  },
};
</script>
<style lang="sass" scoped>
.tree
  width: fit-content
  &__options
    margin-left: 20px
  &__child
    margin-left: 20px
  &__width
    min-width: 468px
    width: fit-content
    max-width: 800px
  &__connection
    position: relative
    &::before
      content: ""
      display: block
      position: absolute
      width: 8px
      height: calc(50% + 10px)
      border-bottom-left-radius: 6px
      border-left: 1px solid $blue-3
      border-bottom: 1px solid $blue-3
      margin-left: -20px
      margin-top: -8px
      bottom: 50%
      pointer-events: none
      transform: translateZ(0)
      background: transparent
  &__option
    &__first
      >.tree__option__connection
        &::before
          height: 62%
    &__connection
      position: relative
      &::before
        content: ""
        display: block
        position: absolute
        width: 8px
        height: calc(100% + 12px)
        border-bottom-left-radius: 6px
        border-left: 1px solid $blue-3
        border-bottom: 1px solid $blue-3
        margin-left: -20px
        margin-top: -8px
        bottom: 50%
        pointer-events: none
        transform: translateZ(0)
        background: transparent
      &-brothers
        >.tree__child
          position: relative
          &::before
            content: ""
            display: block
            position: absolute
            width: 8px
            height: calc(100% + 50px)
            border-left: 1px solid $blue-3
            margin-left: -27px
            margin-top: -50px
            pointer-events: none
            transform: translateZ(0)
            background: transparent

.simple__node__connection::before
  height: 70%

  
</style>
